/**
 * Projetos - Página Principal (Refatorada)
 * 
 * Este componente foi refatorado para usar componentes modulares:
 * - DashboardProjetos: métricas resumidas
 * - ListaProjetos: grid de cards
 * - CardProjeto: card individual
 * - FiltrosProjetos: busca e filtros
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { CurrencyInput, formatMoney } from '../components/ui/currency-input';
import { HelpTooltip, TitleWithHelp } from '../components/ui/help-tooltip';
import { 
  Plus, Ship, Calendar, DollarSign, Edit, Trash2, 
  ExternalLink, ChevronRight, Users, Anchor, FileText, 
  ClipboardList, Search, Filter, CheckCircle2, AlertTriangle, 
  Clock, X, Loader2, Copy, Paperclip, Wallet, TrendingUp, TrendingDown, Package, Info
} from 'lucide-react';
import { toast } from 'sonner';

// Importar componentes modulares
import {
  STATUS_CONFIG,
  PROJETO_FORM_INICIAL,
  getSpecStatus,
  formatDate,
  DashboardProjetos,
  ListaProjetos,
  FiltrosProjetos
} from '../components/projetos';
import AnexosManager from '../components/AnexosManager';
import AnexosErrorBoundary from '../components/AnexosErrorBoundary';

const Projects = () => {
  const navigate = useNavigate();
  
  // Estados principais
  const [projects, setProjects] = useState([]);
  const [factories, setFactories] = useState([]);
  const [vesselModels, setVesselModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteProjectId, setDeleteProjectId] = useState(null);
  const [activeTab, setActiveTab] = useState('geral');
  const [formData, setFormData] = useState(PROJETO_FORM_INICIAL);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('todos');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filterModels, setFilterModels] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  
  // Dialog de seleção de categorias
  const [showCategoriaDialog, setShowCategoriaDialog] = useState(false);
  const [selectedModelForEmb, setSelectedModelForEmb] = useState(null);
  const [categoriasDisponiveis, setCategoriasDisponiveis] = useState([]);
  const [categoriasSelecionadas, setCategoriasSelecionadas] = useState([]);
  
  // CRM Clientes para vincular ao projeto
  const [crmClientes, setCrmClientes] = useState([]);
  const [clienteSearch, setClienteSearch] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [quickForm, setQuickForm] = useState({ nome: '', tipo_pessoa: 'juridica', cnpj: '', cpf: '', email: '', telefone: '' });
  const [quickLoading, setQuickLoading] = useState(false);

  // Valores derivados
  const uniqueClients = useMemo(() => {
    const clients = [...new Set(projects.map(p => p.client).filter(Boolean))];
    return clients.sort();
  }, [projects]);

  const usedModels = useMemo(() => {
    const models = new Set();
    projects.forEach(p => {
      p.embarcacoes?.forEach(e => {
        if (e.vessel_model_nome) models.add(e.vessel_model_nome);
      });
    });
    return Array.from(models).sort();
  }, [projects]);

  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      const matchSearch = !searchTerm || 
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.client?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchClient = filterClient === 'todos' || p.client === filterClient;
      const matchStatus = filterStatus === 'todos' || p.status === filterStatus;
      const matchModels = filterModels.length === 0 || 
        p.embarcacoes?.some(e => filterModels.includes(e.vessel_model_nome));
      
      return matchSearch && matchClient && matchStatus && matchModels;
    });
  }, [projects, searchTerm, filterClient, filterStatus, filterModels]);

  // Funções auxiliares
  const toggleModelFilter = (modelName) => {
    setFilterModels(prev => 
      prev.includes(modelName) 
        ? prev.filter(m => m !== modelName)
        : [...prev, modelName]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterClient('todos');
    setFilterStatus('todos');
    setFilterModels([]);
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [projectsRes, factoriesRes, modelsRes, crmRes] = await Promise.all([
        api.get('/projects'),
        api.get('/factories'),
        api.get('/vessel-models'),
        api.get('/crm-clientes')
      ]);
      setProjects(projectsRes.data);
      setFactories(factoriesRes.data);
      setVesselModels(modelsRes.data);
      setCrmClientes(crmRes.data || []);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handlers de projeto
  const [financeiro, setFinanceiro] = useState(null);
  const [financeiroLoading, setFinanceiroLoading] = useState(false);

  const loadFinanceiro = async (projectId) => {
    if (!projectId) {
      setFinanceiro(null);
      return;
    }
    setFinanceiroLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/financeiro`);
      setFinanceiro(data);
      // Reflete o valor_venda resolvido pelo backend (ex: auto-preenchido pela proposta aceita)
      setFormData((prev) => ({
        ...prev,
        valor_venda: data.valor_venda ?? prev.valor_venda,
        custo_estimado_manual: data.custo_estimado_manual ?? prev.custo_estimado_manual,
        margem_alvo_pct: data.margem_alvo_pct ?? prev.margem_alvo_pct,
      }));
    } catch (error) {
      console.error('Erro ao carregar financeiro:', error);
    } finally {
      setFinanceiroLoading(false);
    }
  };

  const openNewProject = () => {
    setEditingProject(null);
    setFinanceiro(null);
    setFormData({
      ...PROJETO_FORM_INICIAL,
      start_date: new Date().toISOString().split('T')[0]
    });
    setActiveTab('geral');
    setShowDialog(true);
  };

  const openEditProject = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name || '',
      client: project.client || '',
      client_id: project.client_id || '',
      vessel_type: project.vessel_type || '',
      factory_id: project.factory_id || '',
      description: project.description || '',
      google_drive_url: project.google_drive_url || '',
      start_date: project.start_date ? project.start_date.split('T')[0] : '',
      delivery_date: project.delivery_date ? project.delivery_date.split('T')[0] : '',
      budget: project.budget || '',
      valor_venda: project.valor_venda ?? project.budget ?? 0,
      custo_estimado_manual: project.custo_estimado_manual ?? 0,
      margem_alvo_pct: project.margem_alvo_pct ?? '',
      numero_empenho: project.numero_empenho || '',
      responsavel_tecnico: project.responsavel_tecnico || '',
      embarcacoes: project.embarcacoes || [],
      anexos: project.anexos || []
    });
    setActiveTab('geral');
    setShowDialog(true);
    loadFinanceiro(project.id);
  };

  // Deep-link (ex.: link de WhatsApp do Z-API): /projects?project_id=xxx&tab=financeiro
  // abre o projeto já selecionado direto na aba desejada (ex: Financeiro).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('project_id');
    if (!projectId) return;
    const tab = params.get('tab') || 'geral';
    api.get(`/projects/${projectId}`)
      .then((r) => {
        openEditProject(r.data);
        setActiveTab(tab);
      })
      .catch(() => toast.error('Projeto do link não foi encontrado'))
      .finally(() => {
        params.delete('project_id');
        params.delete('tab');
        const rest = params.toString();
        window.history.replaceState({}, '', rest ? `${window.location.pathname}?${rest}` : window.location.pathname);
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.client) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    try {
      const payload = {
        ...formData,
        client_id: formData.client_id || '',
        factory_id: formData.factory_id || '',
        factory_name: factories.find(f => f.id === formData.factory_id)?.name || '',
        vessel_type: formData.embarcacoes.length > 0 
          ? `${formData.embarcacoes.length} embarcação(ões)` 
          : 'Embarcação',
        budget: parseFloat(formData.valor_venda) || parseFloat(formData.budget) || 0,
        valor_venda: parseFloat(formData.valor_venda) || 0,
        custo_estimado_manual: parseFloat(formData.custo_estimado_manual) || 0,
        margem_alvo_pct: formData.margem_alvo_pct === '' || formData.margem_alvo_pct == null
          ? null
          : parseFloat(formData.margem_alvo_pct),
        start_date: formData.start_date || new Date().toISOString(),
        delivery_date: formData.delivery_date || new Date().toISOString()
      };

      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, payload);
        toast.success('Projeto atualizado!');
      } else {
        await api.post('/projects', payload);
        toast.success('Projeto criado!');
      }

      setShowDialog(false);
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar projeto');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/projects/${deleteProjectId}`);
      toast.success('Projeto excluído!');
      setDeleteProjectId(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir projeto');
    }
  };

  // Handlers de embarcação
  const openAddEmbarcacaoDialog = () => {
    setSelectedModelForEmb(null);
    setCategoriasDisponiveis([]);
    setCategoriasSelecionadas([]);
    setShowCategoriaDialog(true);
  };

  const onSelectModelForEmb = (modelId) => {
    const model = vesselModels.find(m => m.id === modelId);
    setSelectedModelForEmb(model);
    
    if (model && model.checklist_base) {
      const categorias = [...new Set(model.checklist_base.map(item => item.categoria))];
      setCategoriasDisponiveis(categorias);
      setCategoriasSelecionadas(categorias);
    } else {
      setCategoriasDisponiveis([]);
      setCategoriasSelecionadas([]);
    }
  };

  const confirmAddEmbarcacao = () => {
    if (!selectedModelForEmb) {
      toast.error('Selecione um modelo de embarcação');
      return;
    }

    const checklistFiltrado = selectedModelForEmb.checklist_base
      ?.filter(item => categoriasSelecionadas.includes(item.categoria))
      .map(item => ({
        ...item,
        tipo_item: item.tipo_item || 'especificacao',
        custo_manual: 0,
        itens_custo: [],
        custo_total: 0
      })) || [];

    const startNum = formData.embarcacoes.length + 1;
    const novaEmbarcacao = {
      id: crypto.randomUUID(),
      numero: startNum,
      vessel_model_id: selectedModelForEmb.id,
      vessel_model_nome: selectedModelForEmb.nome,
      quantidade: 1,
      status: 'planejamento',
      progress: 0,
      checklist: checklistFiltrado,
      categorias_selecionadas: categoriasSelecionadas,
      observacoes: ''
    };

    setFormData({ ...formData, embarcacoes: [...formData.embarcacoes, novaEmbarcacao] });
    setShowCategoriaDialog(false);
    toast.success(`Embarcação adicionada com ${checklistFiltrado.length} itens`);
  };

  const removeEmbarcacao = (index) => {
    const updated = formData.embarcacoes.filter((_, i) => i !== index);
    updated.forEach((e, i) => e.numero = i + 1);
    setFormData({ ...formData, embarcacoes: updated });
  };

  const duplicarEmbarcacao = (index) => {
    const emb = formData.embarcacoes[index];
    const novaEmbarcacao = {
      ...emb,
      id: crypto.randomUUID(),
      numero: formData.embarcacoes.length + 1,
      status: 'planejamento',
      progress: 0
    };
    setFormData({ ...formData, embarcacoes: [...formData.embarcacoes, novaEmbarcacao] });
    toast.success('Embarcação duplicada');
  };

  // Loading state
  if (loading && projects.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <TitleWithHelp
            title="Projetos"
            description="Gerencie projetos de construção naval. Cada projeto pode conter múltiplas embarcações com especificações detalhadas."
            connections={["Especificações", "Produção", "Cronograma"]}
          >
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Ship className="h-6 w-6 text-blue-600" />
              Projetos
            </h1>
          </TitleWithHelp>
          <p className="text-gray-500 mt-1">
            {projects.length} projetos cadastrados
          </p>
        </div>
      </div>

      {/* Dashboard */}
      <DashboardProjetos projetos={projects} />

      {/* Filtros */}
      <FiltrosProjetos
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterClient={filterClient}
        setFilterClient={setFilterClient}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterModels={filterModels}
        toggleModelFilter={toggleModelFilter}
        clearFilters={clearFilters}
        uniqueClients={uniqueClients}
        usedModels={usedModels}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        onNovoClick={openNewProject}
        totalProjetos={projects.length}
        projetosFiltrados={filteredProjects.length}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />

      {/* Lista de Projetos */}
      <ListaProjetos
        projetos={filteredProjects}
        loading={loading}
        onEdit={openEditProject}
        onDelete={setDeleteProjectId}
        viewMode={viewMode}
      />

      {/* Dialog de Projeto */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProject ? 'Editar Projeto' : 'Novo Projeto'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="geral">Dados Gerais</TabsTrigger>
                <TabsTrigger value="financeiro" data-testid="tab-financeiro">
                  Financeiro
                </TabsTrigger>
                <TabsTrigger value="embarcacoes">
                  Embarcações ({formData.embarcacoes.length})
                </TabsTrigger>
                <TabsTrigger value="anexos" className="flex items-center gap-1">
                  <Paperclip className="h-3 w-3" />
                  Anexos ({formData.anexos?.length || 0})
                </TabsTrigger>
              </TabsList>

              {/* Tab Geral */}
              <TabsContent value="geral" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome do Projeto *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: Lancha XR 450"
                    />
                  </div>
                  <div className="relative">
                    <Label>Cliente *</Label>
                    <Input
                      value={formData.client}
                      onChange={(e) => {
                        setFormData({ ...formData, client: e.target.value, client_id: '' });
                        setClienteSearch(e.target.value);
                        setShowClienteDropdown(true);
                        setShowQuickCreate(false);
                      }}
                      onFocus={() => { setClienteSearch(formData.client); setShowClienteDropdown(true); }}
                      placeholder="Buscar por nome, CNPJ, CPF ou telefone..."
                      autoComplete="off"
                    />
                    {showClienteDropdown && clienteSearch && !showQuickCreate && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto">
                        {crmClientes.filter(c => {
                          const s = clienteSearch.toLowerCase();
                          return c.nome.toLowerCase().includes(s) ||
                            (c.razao_social || '').toLowerCase().includes(s) ||
                            (c.cnpj || '').includes(clienteSearch) ||
                            (c.cpf || '').includes(clienteSearch) ||
                            (c.telefone || '').includes(clienteSearch) ||
                            (c.contatos || []).some(ct => (ct.nome || '').toLowerCase().includes(s) || (ct.telefone || '').includes(clienteSearch));
                        }).slice(0, 8).map(c => (
                          <button key={c.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 transition-colors border-b last:border-b-0"
                            onClick={() => {
                              setFormData({ ...formData, client: c.nome, client_id: c.id });
                              setShowClienteDropdown(false);
                            }}>
                            <div className="font-medium">{c.nome}</div>
                            {c.razao_social && c.razao_social !== c.nome && <div className="text-xs text-gray-400">{c.razao_social}</div>}
                            {(c.cnpj || c.cpf) && <div className="text-xs text-gray-300 font-mono">{c.cnpj || c.cpf}</div>}
                          </button>
                        ))}
                        {crmClientes.filter(c => { const s = clienteSearch.toLowerCase(); return c.nome.toLowerCase().includes(s) || (c.razao_social || '').toLowerCase().includes(s) || (c.cnpj || '').includes(clienteSearch) || (c.cpf || '').includes(clienteSearch) || (c.telefone || '').includes(clienteSearch) || (c.contatos || []).some(ct => (ct.nome || '').toLowerCase().includes(s) || (ct.telefone || '').includes(clienteSearch)); }).length === 0 && (
                          <div className="border-b px-3 py-2 text-xs text-gray-400">Nenhum cliente encontrado no CRM</div>
                        )}
                        {clienteSearch.trim().length >= 2 && (
                          <button type="button"
                            className="w-full text-left px-3 py-2.5 text-sm bg-indigo-50 hover:bg-indigo-100 transition-colors flex items-center gap-2 text-indigo-700 font-medium"
                            onClick={() => {
                              setQuickForm({ nome: clienteSearch.trim(), tipo_pessoa: 'juridica', cnpj: '', cpf: '', email: '', telefone: '' });
                              setShowQuickCreate(true);
                              setShowClienteDropdown(false);
                            }}>
                            <Plus className="h-4 w-4" />
                            Cadastrar "{clienteSearch.trim()}" como novo cliente
                          </button>
                        )}
                      </div>
                    )}
                    {formData.client_id && <div className="text-xs text-green-600 mt-0.5">Vinculado ao CRM</div>}

                    {/* Mini-formulário de cadastro rápido */}
                    {showQuickCreate && (
                      <div className="absolute z-50 w-full mt-1 bg-white border-2 border-indigo-300 rounded-lg shadow-xl p-3 space-y-2.5">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-bold text-indigo-700 flex items-center gap-1"><Plus className="h-4 w-4" />Cadastro Rápido</span>
                          <button type="button" onClick={() => setShowQuickCreate(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                        </div>
                        <div><label className="text-xs text-gray-500">Nome *</label><Input value={quickForm.nome} onChange={e => setQuickForm(p => ({...p, nome: e.target.value}))} className="h-8 text-sm" /></div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Tipo</label>
                            <select value={quickForm.tipo_pessoa} onChange={e => setQuickForm(p => ({...p, tipo_pessoa: e.target.value}))} className="w-full h-8 text-sm border rounded-md px-2">
                              <option value="juridica">Jurídica</option>
                              <option value="fisica">Física</option>
                              <option value="estrangeiro">Estrangeiro</option>
                            </select>
                          </div>
                          {quickForm.tipo_pessoa === 'juridica' && <div><label className="text-xs text-gray-500">CNPJ</label><Input value={quickForm.cnpj} onChange={e => setQuickForm(p => ({...p, cnpj: e.target.value}))} placeholder="00.000.000/0000-00" className="h-8 text-sm" /></div>}
                          {quickForm.tipo_pessoa === 'fisica' && <div><label className="text-xs text-gray-500">CPF</label><Input value={quickForm.cpf} onChange={e => setQuickForm(p => ({...p, cpf: e.target.value}))} placeholder="000.000.000-00" className="h-8 text-sm" /></div>}
                          {quickForm.tipo_pessoa === 'estrangeiro' && <div><label className="text-xs text-gray-500">ID Estrangeiro</label><Input value={quickForm.cpf} onChange={e => setQuickForm(p => ({...p, cpf: e.target.value}))} className="h-8 text-sm" /></div>}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><label className="text-xs text-gray-500">E-mail</label><Input value={quickForm.email} onChange={e => setQuickForm(p => ({...p, email: e.target.value}))} className="h-8 text-sm" /></div>
                          <div><label className="text-xs text-gray-500">Telefone</label><Input value={quickForm.telefone} onChange={e => setQuickForm(p => ({...p, telefone: e.target.value}))} className="h-8 text-sm" /></div>
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                          <button type="button" onClick={() => setShowQuickCreate(false)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">Cancelar</button>
                          <button type="button" disabled={quickLoading || !quickForm.nome.trim()} className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-md hover:bg-indigo-700 disabled:opacity-50 font-medium"
                            onClick={async () => {
                              setQuickLoading(true);
                              try {
                                const payload = { nome: quickForm.nome.trim(), tipo_pessoa: quickForm.tipo_pessoa, status: 'ativo', email: quickForm.email, telefone: quickForm.telefone };
                                if (quickForm.tipo_pessoa === 'juridica') payload.cnpj = quickForm.cnpj;
                                else if (quickForm.tipo_pessoa === 'fisica') payload.cpf = quickForm.cpf;
                                else payload.id_estrangeiro = quickForm.cpf;
                                const res = await api.post('/crm-clientes', payload);
                                const novo = res.data;
                                setCrmClientes(prev => [...prev, novo]);
                                setFormData({ ...formData, client: novo.nome, client_id: novo.id });
                                setShowQuickCreate(false);
                                toast.success(`"${novo.nome}" cadastrado no CRM e vinculado ao projeto`);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Erro ao cadastrar');
                              } finally { setQuickLoading(false); }
                            }}>
                            {quickLoading ? 'Salvando...' : 'Cadastrar e Vincular'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Data de Início</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Data de Entrega</Label>
                    <Input
                      type="date"
                      value={formData.delivery_date}
                      onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                    />
                  </div>
                </div>

                {editingProject && editingProject.created_at && (
                  <div className="bg-slate-50 border rounded-lg px-3 py-2 text-sm text-slate-600 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-400" />
                    Projeto criado em: <strong>{new Date(editingProject.created_at).toLocaleDateString('pt-BR')}</strong>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fábrica</Label>
                    <Select
                      value={formData.factory_id}
                      onValueChange={(v) => setFormData({ ...formData, factory_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {factories.map(f => (
                          <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Nº Empenho / Contrato</Label>
                      <HelpTooltip 
                        description="Número de identificação do contrato ou ordem de compra do cliente. Usado para rastreabilidade financeira."
                        iconSize={14}
                      />
                    </div>
                    <Input
                      value={formData.numero_empenho || ''}
                      onChange={(e) => setFormData({ ...formData, numero_empenho: e.target.value })}
                      placeholder="2025/001"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Responsável Técnico</Label>
                    <Input
                      value={formData.responsavel_tecnico || ''}
                      onChange={(e) => setFormData({ ...formData, responsavel_tecnico: e.target.value })}
                      placeholder="Nome do responsável"
                    />
                  </div>
                </div>

                <div>
                  <Label>Descrição</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
              </TabsContent>

              {/* Tab Financeiro */}
              <TabsContent value="financeiro" className="space-y-4 mt-4" data-testid="tabcontent-financeiro">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Valor de Venda (R$)</Label>
                      <HelpTooltip
                        description="Valor do contrato/proposta aprovada com o cliente. Preenchido automaticamente quando o projeto vem de uma oportunidade do CRM com proposta aceita, mas pode ser editado livremente a qualquer momento."
                        iconSize={14}
                      />
                    </div>
                    <CurrencyInput
                      data-testid="input-valor-venda"
                      value={formData.valor_venda}
                      onChange={(v) => setFormData({ ...formData, valor_venda: v })}
                    />
                    {financeiro?.valor_venda_origem === 'proposta_aceita' && (
                      <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Preenchido automaticamente pela proposta aceita
                      </p>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Custo Estimado (R$)</Label>
                      <HelpTooltip
                        description="Orçamento planejado para compras deste projeto. Usado para comparar com o Custo Efetivo (soma real das POs aprovadas) e alertar sobre estouro de orçamento."
                        iconSize={14}
                      />
                    </div>
                    <CurrencyInput
                      data-testid="input-custo-estimado"
                      value={formData.custo_estimado_manual}
                      onChange={(v) => setFormData({ ...formData, custo_estimado_manual: v })}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Margem Alvo (%)</Label>
                      <HelpTooltip
                        description="Margem de lucro desejada sobre o Valor de Venda. O Financeiro é alertado automaticamente quando a Margem Real (Valor de Venda - Custo Efetivo) cai perto ou abaixo desta meta. Deixe em branco para usar o padrão do sistema (15%)."
                        iconSize={14}
                      />
                    </div>
                    <Input
                      data-testid="input-margem-alvo"
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={formData.margem_alvo_pct}
                      onChange={(e) => setFormData({ ...formData, margem_alvo_pct: e.target.value })}
                      placeholder="Padrão do sistema: 15%"
                    />
                  </div>
                </div>

                {!editingProject ? (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-600">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    Custo Efetivo e o Histórico de Pedidos aparecem aqui depois que o projeto for salvo.
                  </div>
                ) : financeiroLoading ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <>
                    {/* Barra de saúde financeira */}
                    <div className="p-4 rounded-lg border bg-white space-y-2" data-testid="barra-saude-financeira">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                          <Wallet className="w-4 h-4 text-indigo-600" />
                          Custo Efetivo x Custo Estimado
                        </div>
                        <span className="text-xs text-slate-500">
                          {financeiro?.percentual_usado != null ? `${financeiro.percentual_usado}%` : '—'}
                        </span>
                      </div>
                      <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            (financeiro?.percentual_usado || 0) >= 100 ? 'bg-red-500' :
                            (financeiro?.percentual_usado || 0) >= 80 ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${Math.min(financeiro?.percentual_usado || 0, 100)}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-sm pt-1">
                        <span className="text-slate-500">
                          Custo Efetivo: <strong className="text-slate-800">{formatMoney(financeiro?.custo_efetivo || 0)}</strong>
                        </span>
                        <span className="text-slate-500">
                          Custo Estimado: <strong className="text-slate-800">{formatMoney(formData.custo_estimado_manual || 0)}</strong>
                        </span>
                      </div>
                      {financeiro?.saldo != null && financeiro.saldo < 0 && (
                        <p className="text-xs text-red-600 flex items-center gap-1 pt-1">
                          <AlertTriangle className="w-3 h-3" /> Estouro de orçamento: {formatMoney(Math.abs(financeiro.saldo))} acima do estimado
                        </p>
                      )}
                    </div>

                    {/* Margem Real x Margem Alvo */}
                    {financeiro?.margem_alvo_efetiva_pct != null && financeiro?.margem_atual_pct != null && (
                      <div className="p-4 rounded-lg border bg-white space-y-2" data-testid="card-margem-real">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                            <TrendingDown className="w-4 h-4 text-indigo-600" />
                            Margem Real x Margem Alvo
                          </div>
                          <span
                            data-testid="margem-atual-valor"
                            className={`text-sm font-semibold ${
                              financeiro.margem_atual_pct >= financeiro.margem_alvo_efetiva_pct ? 'text-emerald-600' :
                              financeiro.margem_atual_pct >= financeiro.margem_alvo_efetiva_pct - 5 ? 'text-amber-600' : 'text-red-600'
                            }`}
                          >
                            {financeiro.margem_atual_pct}% <span className="text-slate-400 font-normal">/ alvo {financeiro.margem_alvo_efetiva_pct}%</span>
                          </span>
                        </div>
                        {financeiro.margem_alvo_origem === 'padrao_sistema' && (
                          <p className="text-xs text-slate-400">Meta própria não definida — usando o padrão do sistema (15%).</p>
                        )}
                        {financeiro.margem_atual_pct < financeiro.margem_alvo_efetiva_pct - 5 && (
                          <p className="text-xs text-red-600 flex items-center gap-1 pt-1">
                            <AlertTriangle className="w-3 h-3" /> Margem crítica: o Financeiro já foi notificado automaticamente
                          </p>
                        )}
                      </div>
                    )}

                    {/* Histórico de Pedidos Vinculados */}
                    <div>
                      <div className="flex items-center gap-2 mb-2 text-sm font-medium text-slate-700">
                        <Package className="w-4 h-4 text-indigo-600" />
                        Histórico de Pedidos Vinculados ({financeiro?.pedidos?.length || 0})
                      </div>
                      {!financeiro?.pedidos?.length ? (
                        <p className="text-sm text-slate-400 py-3 text-center border rounded-lg">Nenhum Pedido de Compra aprovado vinculado ainda.</p>
                      ) : (
                        <div className="border rounded-lg overflow-hidden max-h-56 overflow-y-auto">
                          <table className="w-full text-sm" data-testid="tabela-pedidos-vinculados">
                            <thead className="bg-slate-50 sticky top-0">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium text-slate-600">Pedido</th>
                                <th className="text-left px-3 py-2 font-medium text-slate-600">Fornecedor</th>
                                <th className="text-left px-3 py-2 font-medium text-slate-600">Status</th>
                                <th className="text-right px-3 py-2 font-medium text-slate-600">Total (BRL)</th>
                                <th className="w-8"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {financeiro.pedidos.map((p) => (
                                <tr
                                  key={p.id}
                                  className="border-t cursor-pointer hover:bg-indigo-50 transition-colors"
                                  data-testid={`linha-pedido-vinculado-${p.id}`}
                                  onClick={() => window.open(`/compras?pedido_id=${p.id}`, '_blank')}
                                  title="Abrir este pedido no módulo Compras"
                                >
                                  <td className="px-3 py-2 text-indigo-700 font-medium hover:underline">{p.numero || p.id}</td>
                                  <td className="px-3 py-2">{p.fornecedor_nome || '-'}</td>
                                  <td className="px-3 py-2">
                                    <Badge variant="outline" className="text-xs capitalize">{p.status}</Badge>
                                  </td>
                                  <td className="px-3 py-2 text-right font-medium">{formatMoney(p.total_brl)}</td>
                                  <td className="px-2 py-2 text-slate-400"><ExternalLink className="w-3.5 h-3.5" /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Tab Embarcações */}
              <TabsContent value="embarcacoes" className="space-y-4 mt-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">
                    {formData.embarcacoes.length} embarcação(ões) no projeto
                  </p>
                  <Button type="button" onClick={openAddEmbarcacaoDialog} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Adicionar Embarcação
                  </Button>
                </div>

                {formData.embarcacoes.length === 0 ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center h-32 text-gray-500">
                      <Ship className="h-8 w-8 mb-2 opacity-30" />
                      <p>Nenhuma embarcação adicionada</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {formData.embarcacoes.map((emb, index) => (
                      <Card key={emb.id}>
                        <CardContent className="p-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded bg-blue-100 flex items-center justify-center">
                              <Ship className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium">Embarcação #{emb.numero}</p>
                              <p className="text-sm text-gray-500">
                                {emb.vessel_model_nome || 'Modelo não definido'}
                                {emb.checklist?.length > 0 && ` • ${emb.checklist.length} itens`}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => duplicarEmbarcacao(index)}
                              title="Duplicar"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-red-500"
                              onClick={() => removeEmbarcacao(index)}
                              title="Remover"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Tab Anexos */}
              <TabsContent value="anexos" className="space-y-4 mt-4">
                <AnexosErrorBoundary>
                  <AnexosManager
                    anexos={formData.anexos || []}
                    onChange={(novosAnexos) => setFormData({ ...formData, anexos: novosAnexos })}
                    maxFiles={20}
                    maxSizeMB={50}
                    label="Documentos do Projeto"
                    modulo="projetos"
                    entidadeId={editingProject?.id}
                    categoria="anexos"
                  />
                </AnexosErrorBoundary>
                <p className="text-xs text-gray-500">
                  Adicione documentos relacionados ao projeto: contratos, especificações técnicas, plantas, fotos, etc.
                </p>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingProject ? 'Salvar Alterações' : 'Criar Projeto'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Seleção de Categorias */}
      <Dialog open={showCategoriaDialog} onOpenChange={setShowCategoriaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Embarcação</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Modelo de Embarcação *</Label>
              <Select
                value={selectedModelForEmb?.id || ''}
                onValueChange={onSelectModelForEmb}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um modelo..." />
                </SelectTrigger>
                <SelectContent>
                  {vesselModels.map(model => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.nome} ({model.checklist_base?.length || 0} itens)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {categoriasDisponiveis.length > 0 && (
              <div>
                <Label className="mb-2 block">Categorias do Checklist</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {categoriasDisponiveis.map(cat => (
                    <div key={cat} className="flex items-center gap-2">
                      <Checkbox
                        checked={categoriasSelecionadas.includes(cat)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setCategoriasSelecionadas([...categoriasSelecionadas, cat]);
                          } else {
                            setCategoriasSelecionadas(categoriasSelecionadas.filter(c => c !== cat));
                          }
                        }}
                      />
                      <Label>{cat}</Label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {categoriasSelecionadas.length} de {categoriasDisponiveis.length} categorias selecionadas
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoriaDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={confirmAddEmbarcacao} disabled={!selectedModelForEmb}>
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={!!deleteProjectId} onOpenChange={() => setDeleteProjectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este projeto? Esta ação não pode ser desfeita.
              Todos os dados relacionados (embarcações, especificações) serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Projects;
